import { Link } from 'react-router-dom';
import WishfulMark from '../components/WishfulMark';
import { CONTACT_EMAIL } from '../config/legal';
import {
    APP_DOWNLOAD_URL,
    APP_DOWNLOAD_LABEL,
    APP_DOWNLOAD_NOTE,
    MINIMUM_AGE,
} from '../config/site';

/**
 * The public landing page at `/`.
 *
 * WHY THIS EXISTS: `/` used to `<Navigate to="/feed" />`, which bounced straight on to
 * `/login`. So gifts.memula.com had no public face at all — anyone arriving from an
 * invite email, a shared link, or the App Store "marketing URL" hit a login wall with
 * no explanation of what Wishful is. That is the actual problem behind "the website
 * doesn't match the app"; the domain name was never the issue.
 * See ../../../listful-api/docs/LAUNCH_READINESS.md §2.
 *
 * TWO AUDIENCES, and the second one is easy to forget:
 *   1. A parent who was handed a link and has never heard of this app.
 *   2. An App Review engineer, who needs to understand the surprise mechanic before
 *      they conclude that hiding purchase state from the list owner is a bug.
 *
 * Everything claimed here must actually ship. Do not add aspirational copy — a public
 * page is a promise, and App Review reads it.
 */
export default function Landing() {
    return (
        <div className="min-h-screen bg-white text-slate-800">
            <SiteHeader />
            <Hero />
            <HowItWorks />
            <SurpriseExplainer />
            <Features />
            <ForFamilies />
            <ClosingCta />
            <SiteFooter />
        </div>
    );
}

/* ------------------------------------------------------------------ header */

function SiteHeader() {
    return (
        <header className="border-b border-slate-100">
            <nav className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                    <WishfulMark className="w-8 h-8" />
                    <span className="text-lg font-semibold text-slate-900">Wishful</span>
                </div>
                <div className="flex items-center gap-2 sm:gap-4">
                    <Link
                        to="/login"
                        className="text-sm font-medium text-slate-600 hover:text-slate-900 px-2 py-2"
                    >
                        Sign in
                    </Link>
                    <Link
                        to="/register"
                        className="text-sm font-medium bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700 transition-colors"
                    >
                        Create account
                    </Link>
                </div>
            </nav>
        </header>
    );
}

/* -------------------------------------------------------------------- hero */

function Hero() {
    return (
        <section className="bg-gradient-to-b from-brand-50 to-white">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 text-center">
                <WishfulMark className="w-16 h-16 mx-auto mb-8" />

                <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 max-w-3xl mx-auto">
                    Everyone gets the right gift.
                    <span className="block text-brand-600 mt-2">Nobody spoils the surprise.</span>
                </h1>

                <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
                    Wishful is a shared wish list for the people you buy gifts for. Keep a list
                    of things you'd love, share it with a group, and let everyone else quietly
                    sort out who's buying what — without you ever finding out.
                </p>

                <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center items-center">
                    <Link
                        to="/register"
                        className="w-full sm:w-auto bg-brand-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-brand-700 transition-colors"
                    >
                        Create a free account
                    </Link>
                    <Link
                        to="/login"
                        className="w-full sm:w-auto border border-slate-300 text-slate-700 px-8 py-3 rounded-lg font-medium hover:bg-slate-50 transition-colors"
                    >
                        Sign in
                    </Link>
                </div>

                {/* Rendered only when there is a real link to point at — see config/site.js. */}
                {APP_DOWNLOAD_URL && (
                    <div className="mt-6">
                        <a
                            href={APP_DOWNLOAD_URL}
                            className="text-brand-700 font-medium hover:underline"
                        >
                            {APP_DOWNLOAD_LABEL} →
                        </a>
                        {APP_DOWNLOAD_NOTE && (
                            <p className="text-sm text-slate-500 mt-1">{APP_DOWNLOAD_NOTE}</p>
                        )}
                    </div>
                )}

                <p className="mt-8 text-sm text-slate-500">
                    Free. No ads. We never sell your data.
                </p>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------ how it works */

const STEPS = [
    {
        n: '1',
        title: 'Make your list',
        body: 'Add anything you\'d love — paste a link and Wishful pulls in the photo, title and price for you. Set a size, a colour, a price range, or a note about which one you actually want.',
    },
    {
        n: '2',
        title: 'Share it with a group',
        body: 'Create a group for your family, your friends, or a birthday party and invite people by email. One wish can be shared with several groups, or kept to just one.',
    },
    {
        n: '3',
        title: 'Everyone shops without collisions',
        body: 'People claim what they\'re buying so nobody doubles up. You never see any of it — your own list looks exactly the same to you as the day you wrote it.',
    },
];

function HowItWorks() {
    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <h2 className="text-3xl font-bold text-slate-900 text-center">How it works</h2>
            <div className="mt-12 grid gap-8 md:grid-cols-3">
                {STEPS.map((step) => (
                    <div key={step.n}>
                        <div className="w-10 h-10 rounded-full bg-brand-100 text-brand-700 font-semibold flex items-center justify-center">
                            {step.n}
                        </div>
                        <h3 className="mt-4 text-lg font-semibold text-slate-900">{step.title}</h3>
                        <p className="mt-2 text-slate-600 leading-relaxed">{step.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* --------------------------------------------------------------- the point */

/**
 * The surprise mechanic, spelled out. This section is load-bearing for App Review:
 * from outside, an app that deliberately hides data from the person who owns it looks
 * like a permissions bug. Saying plainly that it is the entire point heads that off.
 */
function SurpriseExplainer() {
    return (
        <section className="bg-slate-50 border-y border-slate-100">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
                <div className="text-center">
                    <span className="inline-block text-sm font-semibold text-brand-700 bg-brand-100 px-3 py-1 rounded-full">
                        The whole idea
                    </span>
                    <h2 className="mt-4 text-3xl font-bold text-slate-900">
                        You can't see what's been bought for you
                    </h2>
                    <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                        That's not an oversight — it's the reason Wishful exists. The moment
                        someone claims or buys something from your list, it disappears from your
                        view. Your list keeps looking untouched, no matter how much of it is
                        already wrapped and in a cupboard somewhere.
                    </p>
                </div>

                <div className="mt-10 grid gap-4 sm:grid-cols-2">
                    <ContrastCard
                        tone="owner"
                        heading="What you see on your own list"
                        items={[
                            'Everything you added, exactly as you added it',
                            'No claims, no purchases, no prices paid',
                            'No idea who has been shopping',
                        ]}
                    />
                    <ContrastCard
                        tone="giver"
                        heading="What everyone else sees"
                        items={[
                            'Who has claimed what, so nobody buys twice',
                            'Who has already bought and wrapped it',
                            'Who else is chipping in on the big one',
                        ]}
                    />
                </div>
            </div>
        </section>
    );
}

function ContrastCard({ tone, heading, items }) {
    const isOwner = tone === 'owner';
    return (
        <div
            className={`rounded-xl border p-6 ${
                isOwner ? 'bg-white border-slate-200' : 'bg-brand-600 border-brand-600'
            }`}
        >
            <h3 className={`font-semibold ${isOwner ? 'text-slate-900' : 'text-white'}`}>
                {heading}
            </h3>
            <ul className="mt-4 space-y-2">
                {items.map((item) => (
                    <li
                        key={item}
                        className={`flex gap-2 text-sm leading-relaxed ${
                            isOwner ? 'text-slate-600' : 'text-brand-50'
                        }`}
                    >
                        <span aria-hidden="true">{isOwner ? '·' : '·'}</span>
                        <span>{item}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ---------------------------------------------------------------- features */

const FEATURES = [
    {
        title: 'Go in on a gift together',
        body: 'For the expensive one. Someone coordinates, everyone else pledges what they can, and Wishful keeps track of who owes what. Money never moves through us — you settle up however you already do.',
    },
    {
        title: 'Recommend a gift',
        body: 'Spotted something perfect for someone? Recommend it to them. They can add it to their own list, or turn it down — and either way it stays between you two.',
    },
    {
        title: 'Add a gift for someone else',
        body: 'Add something to another person\'s list that they can\'t see, so the group can club together on a surprise they never asked for. It reveals itself after the big day.',
    },
    {
        title: 'Birthdays and events',
        body: 'Put the date in the group and tie wishes to it. Everyone gets a nudge before it arrives instead of the night before.',
    },
    {
        title: 'Give to a charity instead',
        body: 'Not everyone wants more things. Ask for a donation to something you care about and let people give that way.',
    },
    {
        title: 'Lists for those who can\'t make their own',
        body: 'Keep a list on behalf of a pet, or a small child, managed entirely from your own account.',
    },
];

function Features() {
    return (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
            <h2 className="text-3xl font-bold text-slate-900 text-center">
                Everything else it does
            </h2>
            <div className="mt-12 grid gap-x-10 gap-y-10 md:grid-cols-2 lg:grid-cols-3">
                {FEATURES.map((f) => (
                    <div key={f.title}>
                        <h3 className="text-lg font-semibold text-slate-900">{f.title}</h3>
                        <p className="mt-2 text-slate-600 leading-relaxed">{f.body}</p>
                    </div>
                ))}
            </div>
        </section>
    );
}

/* ------------------------------------------------------------- kids / ages */

/**
 * The age position, stated publicly and on purpose.
 *
 * Wishful gets used for children's birthdays, so the question of who holds the
 * account is not academic — it decides whether COPPA attaches and what the App Store
 * age rating has to say. The answer is that a child is a list, not a user, managed by
 * a grown-up who already has an account. Say so where parents will read it.
 * See LAUNCH_READINESS.md §7.1.
 */
function ForFamilies() {
    return (
        <section className="bg-brand-50 border-y border-brand-100">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-14">
                <h2 className="text-2xl font-bold text-slate-900">Using Wishful for a child</h2>
                <p className="mt-4 text-slate-700 leading-relaxed">
                    Wishful accounts are for people aged {MINIMUM_AGE} and over. For a younger
                    child, keep their list on <strong>your</strong> account — you add the wishes,
                    you decide which groups see them, and the child never signs in or hands over
                    any details of their own. The rest of the group shops from that list exactly
                    as they would anyone else's.
                </p>
                <p className="mt-4 text-slate-700 leading-relaxed">
                    It means one grown-up stays in control of what is shared and with whom, which
                    is how it should be for a party guest list full of other people's children.
                </p>
                <p className="mt-4 text-sm text-slate-600">
                    Read the{' '}
                    <Link to="/privacy" className="text-brand-700 font-medium hover:underline">
                        privacy policy
                    </Link>{' '}
                    for exactly what we store and how to delete it.
                </p>
            </div>
        </section>
    );
}

/* ----------------------------------------------------------- closing + foot */

function ClosingCta() {
    return (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
            <h2 className="text-3xl font-bold text-slate-900">Start a list</h2>
            <p className="mt-4 text-slate-600 max-w-xl mx-auto leading-relaxed">
                Make an account, add a couple of things you'd actually like, and invite the
                people who ask you what you want every year.
            </p>
            <Link
                to="/register"
                className="mt-8 inline-block bg-brand-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-brand-700 transition-colors"
            >
                Create a free account
            </Link>
        </section>
    );
}

function SiteFooter() {
    return (
        <footer className="border-t border-slate-100 bg-slate-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                    <div className="flex items-center gap-2.5">
                        <WishfulMark className="w-7 h-7" />
                        <span className="font-semibold text-slate-900">Wishful</span>
                    </div>
                    <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                        <Link to="/privacy" className="text-slate-600 hover:text-slate-900">
                            Privacy
                        </Link>
                        <Link to="/support" className="text-slate-600 hover:text-slate-900">
                            Support
                        </Link>
                        <a
                            href={`mailto:${CONTACT_EMAIL}`}
                            className="text-slate-600 hover:text-slate-900"
                        >
                            Contact
                        </a>
                        <Link to="/login" className="text-slate-600 hover:text-slate-900">
                            Sign in
                        </Link>
                    </nav>
                </div>
                <p className="mt-8 text-sm text-slate-500">
                    Wishful is a gift wish-list app. Gifts are coordinated, never paid for,
                    through Wishful — no money changes hands here.
                </p>
            </div>
        </footer>
    );
}
